import React from "react";
import { format } from "date-fns";
import { Edit, Trash, ImageIcon, Lock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ad } from "@/models/ad";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";

// Función para extraer el nombre de usuario del correo electrónico
const extractNameFromEmail = (email: string | null): string => {
  if (!email) return "Desconocido";
  
  // Extraer la parte antes del @ en el correo
  const nameMatch = email.match(/^([^@]+)@/);
  if (nameMatch && nameMatch[1]) {
    // Capitalizar la primera letra
    return nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1);
  }
  
  return "Desconocido";
};

interface AdCardProps {
  ad: Ad;
  openEditDialog: (ad: Ad) => void;
  openDeleteDialog: (ad: Ad) => void;
  canEditOrDeleteAd: (adSellerId: string) => boolean;
}

const AdCard: React.FC<AdCardProps> = ({
  ad,
  openEditDialog,
  openDeleteDialog,
  canEditOrDeleteAd
}) => {
  const { isAdmin } = useAuth();
  
  const isAdExpired = (): boolean => {
    const endDate = new Date(ad.endDate);
    const today = new Date();
    // Normalizar ambas fechas a medianoche
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return endDate <= today;
  };

  // Verificar si el anuncio tiene una URL de material
  const hasMaterialUrl = ad.materialUrl && ad.materialUrl.trim() !== '';

  // Función para abrir URL en nueva pestaña
  const openMaterialUrl = () => {
    if (hasMaterialUrl && ad.materialUrl) {
      window.open(ad.materialUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Obtener el nombre del vendedor (usar el email si el nombre es "Desconocido")
  const displaySellerName = ad.sellerName === "Desconocido" && ad.sellerEmail 
    ? extractNameFromEmail(ad.sellerEmail)
    : ad.sellerName;
    
  // Determine if the ad is expired to apply style
  const expired = isAdExpired();
  const cardClasses = expired 
    ? "border-[0.5px] border-destructive/20 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] bg-destructive/5" 
    : "border-[0.5px] border-gray-200/50 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] bg-card";

  // Determine if the user can edit this ad
  const canEdit = canEditOrDeleteAd(ad.sellerId);
  
  // Only administrators can delete ads
  const canDelete = isAdmin;

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openDeleteDialog(ad);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openEditDialog(ad);
  };

  return (
    <Card className={cardClasses}>
      <CardContent className="p-4">
        {/* Header with name and badge */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex gap-3 items-center">
            <div>
              <h3 className="font-medium text-lg text-foreground">{ad.clientName}</h3>
              <div className="text-sm text-muted-foreground">{displaySellerName}</div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {ad.adType === "courtesy" ? (
              <Badge className="bg-warning text-warning-foreground hover:bg-warning/90">
                Cortesía
              </Badge>
            ) : (
              <Badge variant={expired ? "destructive" : "default"} className={expired ? "" : "bg-success text-success-foreground hover:bg-success/90"}>
                {expired ? "Vencido" : "Venta"}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Ad details */}
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <div>
            <div className="text-muted-foreground font-medium text-xs uppercase">Período</div>
            <div className="text-foreground">{format(new Date(ad.startDate), 'dd/MM/yyyy')} - {format(new Date(ad.endDate), 'dd/MM/yyyy')}</div>
          </div>
          
          <div>
            <div className="text-muted-foreground font-medium text-xs uppercase">Monto</div>
            <div className="font-medium text-foreground">RD$ {new Intl.NumberFormat('es-DO').format(ad.amount)}</div>
          </div>
        </div>
        
        {/* Screens as badges */}
        <div className="mb-4">
          <div className="text-muted-foreground font-medium text-xs uppercase mb-2">Pantallas</div>
          <div className="flex flex-wrap gap-2">
            {ad.screens?.length > 0 ? (
              ad.screens.map((screen, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                >
                  {screen.screenName}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">No hay pantallas</span>
            )}
          </div>
        </div>
        
        {/* Material button */}
        <div className="mt-4 mb-2">
          <Button 
            variant="outline" 
            size="sm" 
            className={`w-full flex items-center justify-center gap-2 ${
              hasMaterialUrl 
                ? 'bg-success/10 text-success hover:bg-success/20 hover:text-success font-medium border-success/20' 
                : 'bg-muted text-muted-foreground border-border'
            }`}
            onClick={openMaterialUrl}
            disabled={!hasMaterialUrl}
          >
            <ExternalLink className="h-4 w-4" />
            {hasMaterialUrl ? "LINK DE MATERIAL ACTIVO" : "SIN MATERIAL"}
          </Button>
        </div>
        
        {/* Action buttons */}
        <div className="flex justify-end mt-4 gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleEdit} 
            disabled={!canEdit} 
            aria-label={canEdit ? "Editar anuncio" : "Solo el vendedor o administrador puede editar"}
            className="hover:bg-muted"
          >
            <Edit className="h-4 w-4" />
          </Button>
          
          {canDelete ? (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleDelete} 
              className="text-destructive hover:text-destructive hover:bg-destructive/10" 
              aria-label="Eliminar anuncio"
            >
              <Trash className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              size="sm" 
              variant="ghost" 
              disabled 
              className="text-muted-foreground" 
              aria-label="Solo administradores pueden eliminar"
              title="Solo administradores pueden eliminar"
            >
              <Lock className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdCard;
